import { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useFirebase } from "@/context/FirebaseProvider" // Replaced useDataUtils
import { getAuth } from "firebase/auth"
import {
  Lead,
  LeadStatus,
  LeadOutcome,
} from "@/components/business/leads/types/LeadTypes"
import {
  TimeFrameOption,
  DEFAULT_TIMEFRAME,
} from "@/components/business/leads/utils/dateFilters"
import { fetchLeadsForBusiness } from "@/utils/firestore/fetchLeads"
import { updateLead } from "@/utils/firestore/leads"
import { useToast } from "@/hooks/use-toast"

const useBusinessDetailsState = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  // Get all accessible businesses, portfolios, and overall loading state from FirebaseProvider
  const { businesses: allAccessibleBusinesses, portfolios, isLoading: isFirebaseLoading } = useFirebase();
  const { toast } = useToast();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [timeFrame, setTimeFrame] = useState<TimeFrameOption>(DEFAULT_TIMEFRAME);
  const [metricsRefreshKey, setMetricsRefreshKey] = useState(0);

  // Determine the current business object based on businessId and allAccessibleBusinesses
  const business = useMemo(() => {
    if (isFirebaseLoading || !businessId || !allAccessibleBusinesses) {
      return null; // Still loading or no data to search
    }
    return allAccessibleBusinesses.find((b) => b.id === businessId) || null;
  }, [businessId, allAccessibleBusinesses, isFirebaseLoading]);

  // Determine loading state for this specific business page
  const businessLoading = useMemo(() => {
    if (isFirebaseLoading) return true; // If FirebaseProvider is still loading, we are loading
    if (!businessId) return false; // No businessId, so not loading anything specific here
    if (business === null && !isFirebaseLoading) return false; // Firebase done, business not found
    return !business; // If business is still null after Firebase loads, it means not found or still processing
  }, [isFirebaseLoading, businessId, business]);
  
  // Determine access: if business is found in allAccessibleBusinesses, user has access.
  const hasAccess = useMemo(() => {
    if (isFirebaseLoading) return null; // Access unknown while loading
    if (!businessId) return null; // No specific business to check access for
    return !!business; // If business object exists, user has access
  }, [isFirebaseLoading, businessId, business]);

  const portfolioTitle = useMemo(() => {
    if (!business?.portfolioId || !portfolios) return ""
    const portfolio = portfolios.find((p) => p.id === business.portfolioId)
    return portfolio?.name || ""
  }, [business, portfolios])

  useEffect(() => {
    if (!businessId) return

    const loadLeads = async () => {
      setIsLoadingLeads(true)
      try {
        const loadedLeads = await fetchLeadsForBusiness(businessId)
        setLeads(loadedLeads)
        setLeadError(null)
      } catch (error: unknown) {
        console.error("Error loading leads:", error)
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load leads"
        setLeadError(errorMessage)
        toast({
          title: "Error",
          description: "Failed to load leads. Please try again.",
          variant: "destructive",
        })
      } finally {
        setIsLoadingLeads(false)
      }
    }

    loadLeads()
  }, [businessId, toast])

  const handleStatusChange = async (id: string, status: LeadStatus) => {
    if (!businessId) return

    try {
      // Update the lead in Firestore
      await updateLead(id, businessId, { status })

      // Update local state
      setLeads((prevLeads) =>
        prevLeads.map((lead) => (lead.id === id ? { ...lead, status } : lead))
      )

      // Refresh metrics
      setMetricsRefreshKey((prev) => prev + 1)
    } catch (error: unknown) {
      console.error("Error updating lead status:", error)
      toast({
        title: "Error",
        description: "Failed to update lead status",
        variant: "destructive",
      })
    }
  }

  const handleOutcomeChange = async (
    id: string,
    outcome: LeadOutcome | null
  ) => {
    if (!businessId) return

    try {
      // Update the lead in Firestore
      await updateLead(id, businessId, { outcome })

      // Update local state
      setLeads((prevLeads) =>
        prevLeads.map((lead) => (lead.id === id ? { ...lead, outcome } : lead))
      )

      // Refresh metrics
      setMetricsRefreshKey((prev) => prev + 1)
    } catch (error: unknown) {
      console.error("Error updating lead outcome:", error)
      toast({
        title: "Error",
        description: "Failed to update lead outcome",
        variant: "destructive",
      })
    }
  }

  const handleValueChange = async (id: string, value: string) => {
    if (!businessId) return

    try {
      // Update the lead in Firestore
      await updateLead(id, businessId, { value })

      // Update local state
      setLeads((prevLeads) =>
        prevLeads.map((lead) => (lead.id === id ? { ...lead, value } : lead))
      )

      // Refresh metrics
      setMetricsRefreshKey((prev) => prev + 1)
    } catch (error: unknown) {
      console.error("Error updating lead value:", error)
      toast({
        title: "Error",
        description: "Failed to update lead value",
        variant: "destructive",
      })
    }
  }

  const handleNameChange = async (id: string, name: string) => {
    if (!businessId) return

    try {
      // Update the lead in Firestore
      await updateLead(id, businessId, { name })

      // Update local state
      setLeads((prevLeads) =>
        prevLeads.map((lead) => (lead.id === id ? { ...lead, name } : lead))
      )
    } catch (error: unknown) {
      console.error("Error updating lead name:", error)
      toast({
        title: "Error",
        description: "Failed to update lead name",
        variant: "destructive",
      })
    }
  }

  const handleSearch = (term: string) => {
    setSearchTerm(term)
  }

  const handleFilter = () => {
    // Filter action placeholder
  }

  const handleEditCampaign = () => {
    if (businessId) {
      navigate(`/business/${businessId}/campaigns`)
    }
  }

  const handleEditGoPlan = () => {
    if (businessId) {
      navigate(`/business/${businessId}/go-plan`)
    }
  }

  const handleEditGoCampaign = () => {
    if (businessId) {
      navigate(`/business/${businessId}/gocampaign?step=1`)
    }
  }

  return {
    business,
    portfolioTitle,
    timeFrame,
    setTimeFrame,
    searchTerm,
    setSearchTerm,
    leads,
    isLoadingLeads,
    leadError,
    businessLoading,
    metricsRefreshKey,
    hasAccess,
    handleStatusChange,
    handleOutcomeChange,
    handleValueChange,
    handleNameChange,
    handleEditCampaign,
    handleEditGoPlan,
    handleEditGoCampaign,
    handleFilter,
    handleSearch,
  }
}

export default useBusinessDetailsState
